// Types
import { type RouteRecordRaw } from 'vue-router';
import headerRoutes from '@/modules/header/header.routes.children';
import { prefixRouteNames } from '@/shared/utils/routes';
import { useUserStore } from '@/shared/stores/user.store';

export default [
    {
        path: 'administration',
        component: () => import('./components/AdministrationContent.vue'),
        name: 'administration',
        meta: {
            title: 'Administration',
            sideNav: {
                key: 'administration',
            }
        },
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            // Allow access for admins, super-admins, project managers (CDP), and workspace managers
            // Workspace managers must have at least one managed workspace
            const hasAccess = userStore.isAdmin
                || userStore.isSuperAdmin
                || (userStore.isProjectManager
                && userStore.isWorkspaceManager);

            if (hasAccess) {
                // For CDP or workspace managers (not admin/super-admin), redirect to teams tab if no view is specified
                if ((userStore.isProjectManager || userStore.isWorkspaceManager)
                    && !userStore.isAdmin
                    && !userStore.isSuperAdmin
                    && !to.query.view) {
                    return next({
                        ...to,
                        query: {
                            ...to.query,
                            view: 'teams'
                        }
                    });
                }
                return next();
            }

            return next({
                name: 'dashboard',
                query: to.query
            });
        },
        children: [
            // Users Tab routes
            {
                path: 'send-credentials',
                name: 'send-credentials',
                component: () => import('./components/modals/UsersTab/SendCredentials/SendCredentialsModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'import',
                name: 'import',
                component: () => import('./components/modals/UsersTab/Import/ImportModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'delete',
                name: 'delete',
                component: () => import('./components/modals/UsersTab/Delete/DeleteModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'take-control',
                name: 'take-control',
                component: () => import('./components/modals/UsersTab/TakeControl/TakeControlModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'user-form',
                name: 'user-form',
                component: () => import('./components/modals/UsersTab/UserForm/AddUserFormModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'edit-user/:userId',
                name: 'edit-user',
                component: () => import('./components/modals/UsersTab/UserForm/EditUserFormModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'duplicate-user/:userId',
                name: 'duplicate-user',
                component: () => import('./components/modals/UsersTab/UserForm/DuplicateUserFormModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'handle-environments',
                name: 'handle-environments',
                component: () => import('./components/modals/UsersTab/Environment/ListEnvironment/HandleEnvironmentsMain.vue'),
                meta: {
                    modal: true,
                },
                children: [
                    {
                        path: 'add-environment',
                        name: 'add-environment',
                        component: () => import('./components/modals/UsersTab/Environment/AddEnvironment/AddEnvironmentMain.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                    {
                        path: 'edit-environment/:envId',
                        name: 'edit-environment',
                        component: () => import('./components/modals/UsersTab/Environment/EditEnvironment/EditEnvironmentMain.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                    {
                        path: 'delete-environment/:envId',
                        name: 'delete-environment',
                        component: () => import('./components/modals/UsersTab/Environment/DeleteEnvironment/DeleteEnvironment.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                ],
            },
            // Teams Tab routes
            {
                path: 'handle-missions',
                name: 'handle-missions',
                component: () => import('./components/modals/TeamsTab/HandleWorkspaceMissions/HandleWorkspaceMissionsModal.vue'),
                meta: {
                    modal: true,
                },
                children: [
                    {
                        path: 'add-missions',
                        name: 'add-missions',
                        component: () => import('./components/modals/TeamsTab/HandleWorkspaceMissions/HandleWorkspaceMissionsAddModal.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                    {
                        path: 'delete-mission/:missionId',
                        name: 'delete-mission',
                        component: () => import('./components/modals/TeamsTab/HandleWorkspaceMissions/HandleDeleteMissionModal.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                    {
                        path: 'confirm-mission-update',
                        name: 'confirm-mission-update-handle-workspace-missions',
                        component: () => import('./components/modals/TeamsTab/ConfirmUpdateMissions/ConfirmUpdateMissionsModal.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                ]
            },
            {
                path: 'add-teams/:workspaceId/:targetTeam',
                name: 'add-teams',
                component: () => import('./components/modals/TeamsTab/AddTeam/AddTeamModal.vue'),
                meta: {
                    modal: true,
                },
                props: true,
            },
            {
                path: 'rename-team/:teamId',
                name: 'rename-team',
                component: () => import('./components/modals/TeamsTab/RenameTeam/RenameTeamModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'handle-authorizations/:targetIds',
                name: 'handle-authorizations',
                component: () => import('./components/modals/TeamsTab/HandleAuthorizations/HandleAuthorizationsModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'add-user-to-workspace/:targetTeam',
                name: 'add-user-to-workspace',
                component: () => import('./components/modals/TeamsTab/AddUserToWorkspace/AddUserToWorkspaceModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'assign-missions/:targetIds',
                name: 'assign-missions',
                component: () => import('./components/modals/TeamsTab/AssignMissions/AssignMissionsModal.vue'),
                meta: {
                    modal: true,
                },
                children: [
                    {
                        path: 'confirm-mission-update',
                        name: 'confirm-mission-update-assign-missions',
                        component: () => import('./components/modals/TeamsTab/ConfirmUpdateMissions/ConfirmUpdateMissionsModal.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                ]
            },
            {
                path: 'delete-team/:workspaceId/:teamId',
                name: 'delete-team',
                component: () => import('./components/modals/TeamsTab/DeleteTeam/DeleteTeamModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'edit-actor/:actorId',
                name: 'edit-actor',
                component: () => import('./components/modals/TeamsTab/EditActor/EditActorModal.vue'),
                meta: {
                    modal: true,
                },
                children: [
                    {
                        path: 'confirm-mission-update',
                        name: 'confirm-mission-update-edit-actor',
                        component: () => import('./components/modals/TeamsTab/ConfirmUpdateMissions/ConfirmUpdateMissionsModal.vue'),
                        meta: {
                            modal: true,
                        },
                    },
                ]
            },
            {
                path: 'update-workspace-missions/:workspaceId',
                name: 'update-workspace-missions',
                component: () => import('./components/modals/TeamsTab/UpdateWorkspaceMissions/UpdateWorkspaceMissionsModal.vue'),
                meta: {
                    modal: true,
                },
            },
            {
                path: 'delete-actors-from-team/:targets',
                name: 'delete-actors-from-team',
                component: () => import('./components/modals/TeamsTab/DeleteActorFromTeam/DeleteActorFromTeamModal.vue'),
                meta: {
                    modal: true,
                },
            },
            ...prefixRouteNames('administration', headerRoutes),
        ],
    },
] satisfies RouteRecordRaw[];
